// src/lib/utils/date.ts

/**
 * Validates if a string is a valid ISO date format
 * @param dateString String to validate
 * @returns True if valid ISO date string
 */
function isValidISODate(dateString: string): boolean {
  const date = new Date(dateString)
  return date instanceof Date && !isNaN(date.getTime())
}

/**
 * Format options for date strings
 * @typedef DateFormat
 * @description Defines the available date formatting options:
 * - 'full': Complete date (e.g., "February 25, 2024")
 * - 'monthDay': Month and day only (e.g., "February 25")
 * - 'shortMonthDay': Abbreviated month and day (e.g., "Feb 25")
 * - 'shortMonthYear': Abbreviated month and two-digit year (e.g., "Feb '24")
 * - 'monthYear': Month and year only (e.g., "February 2024")
 * - 'short': Standard US short date format (e.g., "2/25/2024")
 * - 'iso': ISO date format (e.g., "2024-02-25")
 * - 'datetime': Full ISO datetime (e.g., "2024-02-25T00:00:00.000Z")
 * - 'relative': Human-readable relative time (e.g., "2 days ago")
 */
export type DateFormat = 
  | 'full'           // February 25, 2024
  | 'monthDay'       // February 25
  | 'shortMonthDay'  // Feb 25
  | 'shortMonthYear' // Feb '24
  | 'monthYear'      // February 2024
  | 'short'          // 2/25/2024
  | 'iso'           // 2024-02-25
  | 'datetime'      // 2024-02-25T00:00:00.000Z
  | 'relative'      // 2 days ago

/**
 * Format a date consistently across server and client
 * Uses UTC to ensure consistent rendering and avoid timezone issues
 * @param {string | Date} date - The date to format
 * @param {DateFormat} format - The desired format (defaults to 'full')
 * @returns {string} The formatted date string
 * @example
 * formatDate('2024-02-25', 'full') // "February 25, 2024"
 * formatDate('2024-02-25', 'shortMonthDay') // "Feb 25"
 * formatDate('2024-02-25', 'short') // "2/25/2024"
 */
export function formatDate(date: string | Date, format: DateFormat = 'full'): string {
  if (typeof date === 'string' && !isValidISODate(date)) {
    throw new Error(`Invalid date format: ${date}`)
  }

  if (format === 'iso') return formatDateISO(date)
  if (format === 'datetime') return formatDateTime(date)
  if (format === 'relative') return formatRelativeDate(date)

  const d = new Date(date)
  
  // Ensure we're working with a UTC date to prevent timezone issues
  // Create a fixed-point UTC timestamp
  // 
  // IMPORTANT: Previous approach used d.getTimezoneOffset() which caused hydration mismatches:
  // - getTimezoneOffset() returns different values on server vs client when they're in different timezones
  // - This led to different date calculations during SSR vs client hydration
  // - The calculation d.getTime() - d.getTimezoneOffset() * 60000 would produce different results
  //
  // This new approach:
  // - Extracts UTC components directly using getUTC* methods which are timezone-independent
  // - Reconstructs a date using Date.UTC() which creates a timestamp in UTC
  // - Ensures identical date representation regardless of where the code runs (server or client)
  // - Completely avoids timezone offset calculations that could differ between environments
  const utcYear = d.getUTCFullYear()
  const utcMonth = d.getUTCMonth()
  const utcDay = d.getUTCDate()
  const utcDate = new Date(Date.UTC(utcYear, utcMonth, utcDay, 0, 0, 0))

  const options: Intl.DateTimeFormatOptions = {
    timeZone: 'UTC', // Always use UTC for consistent rendering
    ...getFormatOptions(format)
  }

  // Create a fixed formatter that will not be affected by the client's timezone
  return new Intl.DateTimeFormat('en-US', options).format(utcDate)
}

/**
 * Get format options for different date formats
 * @private
 * @param {DateFormat} format - The desired format
 * @returns {Intl.DateTimeFormatOptions} Options for Intl.DateTimeFormat
 */
function getFormatOptions(format: DateFormat): Intl.DateTimeFormatOptions {
  switch (format) {
    case 'full':
      return {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }
    case 'monthDay':
      return {
        month: 'long',
        day: 'numeric'
      }
    case 'shortMonthDay':
      return {
        month: 'short',
        day: 'numeric'
      }
    case 'shortMonthYear':
      return {
        month: 'short',
        year: '2-digit'
      }
    case 'monthYear':
      return {
        month: 'long',
        year: 'numeric'
      }
    case 'short':
      return {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
      }
    case 'iso':
    case 'datetime':
    case 'relative':
      // These formats are handled in the main formatDate function
      // Return default format options as fallback
      return {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }
    default:
      return {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }
  }
}

/**
 * Format a date string for meta tags and SEO
 * @param {string | Date} date - The date to format
 * @returns {string} ISO formatted date string (YYYY-MM-DD)
 * @example
 * formatDateISO('2024-02-25T12:00:00Z') // "2024-02-25"
 */
export function formatDateISO(date: string | Date): string {
  const d = new Date(date)
  const isoString = d.toISOString()
  const [datePart] = isoString.split('T')
  if (!datePart) {
    throw new Error('Failed to format date as ISO string')
  }
  return datePart
}

/**
 * Format a date for datetime attribute
 * @param {string | Date} date - The date to format
 * @returns {string} Full ISO datetime string
 * @example
 * formatDateTime('2024-02-25') // "2024-02-25T00:00:00.000Z"
 */
export function formatDateTime(date: string | Date): string {
  return new Date(date).toISOString()
}

/**
 * Compare two dates for sorting
 * @param {string | Date} a - First date
 * @param {string | Date} b - Second date
 * @returns {number} Positive if b is later, negative if a is later
 * @example
 * [dates].sort(compareDates) // Sorts in descending order (newest first)
 */
export function compareDates(a: string | Date, b: string | Date): number {
  return new Date(b).getTime() - new Date(a).getTime()
}

/**
 * Get days since a date
 * @param {string | Date} date - The date to compare against now
 * @returns {number} Number of days between the date and now
 * @example
 * getDaysSince('2024-02-20') // 5 (if today is Feb 25)
 */
export function getDaysSince(date: string | Date): number {
  const then = new Date(date).getTime()
  const now = Date.now()
  return Math.floor((now - then) / (1000 * 60 * 60 * 24))
}

/**
 * Check if a date string is valid
 * @param {string} dateString - The date string to validate
 * @returns {boolean} True if the date is valid
 * @example
 * isValidDate('2024-02-25') // true
 * isValidDate('invalid') // false
 */
export function isValidDate(dateString: string): boolean {
  const date = new Date(dateString)
  return date instanceof Date && !isNaN(date.getTime())
}

/**
 * Format a date relative to now (e.g., "2 days ago")
 * @param {string | Date} date - The date to format
 * @returns {string} Human-readable relative time
 * @example
 * formatRelativeDate('2024-02-23') // "2 days ago"
 */
export function formatRelativeDate(date: string | Date): string {
  const days = getDaysSince(date)
  
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days} days ago`
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`
  if (days < 365) return `${Math.floor(days / 30)} months ago`
  return `${Math.floor(days / 365)} years ago`
}

/**
 * Parse a date string into a Date object
 * Handles various formats and ensures UTC
 * @param {string | Date} date - The date to parse
 * @returns {Date} Parsed Date object
 * @example
 * parseDate('2024-02-25') // Date object
 * @deprecated Use safeParseDate instead for better error handling
 */
export function parseDate(date: string | Date): Date {
  if (date instanceof Date) return date
  return new Date(date)
}

/**
 * Safely converts a date-like value to a Date object.
 * 
 * @param {Date | string | number} dateValue - The date value to convert
 * @returns {Date | null} Date object or null if invalid
 */
export function safeParseDate(dateValue: Date | string | number): Date | null {
  try {
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : dateValue
    }
    
    const parsed = new Date(dateValue)
    return isNaN(parsed.getTime()) ? null : parsed
  } catch {
    return null
  }
}

/**
 * Format a date for sorting (YYYY-MM-DD)
 * @param {string | Date} date - The date to format
 * @returns {string} Sortable date string
 * @example
 * formatSortableDate('2024-02-25T12:00:00Z') // "2024-02-25"
 */
export function formatSortableDate(date: string | Date): string {
  return formatDateISO(date)
}

/**
 * Get start of day in UTC
 * @param {string | Date} date - The date to process
 * @returns {Date} Date object set to start of day in UTC
 * @example
 * getStartOfDay('2024-02-25T12:34:56Z') // 2024-02-25T00:00:00.000Z
 */
export function getStartOfDay(date: string | Date): Date {
  const d = new Date(date)
  d.setUTCHours(0, 0, 0, 0)
  return d
}

/**
 * Get end of day in UTC
 * @param {string | Date} date - The date to process
 * @returns {Date} Date object set to end of day in UTC
 * @example
 * getEndOfDay('2024-02-25T12:34:56Z') // 2024-02-25T23:59:59.999Z
 */
export function getEndOfDay(date: string | Date): Date {
  const d = new Date(date)
  d.setUTCHours(23, 59, 59, 999)
  return d
}
/**
 * Normalize a bare `YYYY-MM-DD` date to a full ISO instant at UTC midnight.
 *
 * Schema.org date fields (e.g. VideoObject `uploadDate`) want a full datetime;
 * a date-only value is left-padded with `T00:00:00Z`. Any value already
 * carrying a time (or otherwise not matching the date-only shape) passes
 * through unchanged. Shared by the video/framework structured-data builders.
 *
 * @param {string} value - A date string, possibly date-only.
 * @returns {string} An ISO instant, or the input unchanged.
 */
export function normalizeIsoDate(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00Z` : value
}
